import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Mail, AlertCircle, CheckCircle2 } from 'lucide-react';

/**
 * Forgot-password screen — clean EG3 brand chrome (iter114s.68).
 *
 * Replaces the previous medieval theme (knight artwork +
 * Cormorant Garamond italics + "raven dispatched" copy) with the
 * standard EG3 surface used by Login, Splash, Pricing Catalog —
 * pale-green gradient backdrop, the circular EG3 logo, lucide icons,
 * plain conversational copy.
 *
 * Functional logic (single email field, anti-enumeration success
 * state, rate-limit handling, no retry from success state) is
 * unchanged. All data-testids are preserved.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError('Too many requests. Please wait 15 minutes and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#E8F1E6] via-[#F4F7F2] to-white flex items-center justify-center px-4 py-10"
      data-testid="forgot-password-page-medieval"
    >
      <div className="w-full max-w-md">
        {/* Logo + title */}
        <div className="text-center mb-6">
          <Link to="/login" data-testid="forgot-logo-link" className="inline-block">
            <img
              src="/eg3-logo.png"
              alt="Evergreen Graphics Group"
              className="h-20 w-20 mx-auto mb-3 rounded-full ring-2 ring-[#6FBF73]/30 object-contain bg-white"
            />
          </Link>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#14281D] tracking-tight"
            data-testid="forgot-heading"
          >
            Reset your password
          </h1>
          <p
            className="mt-2 text-sm md:text-base text-[#1F3D2C]/75"
            data-testid="forgot-subheading"
          >
            Enter your account email and we'll send you a reset link.
          </p>
        </div>

        {/* Form card */}
        <div
          className="bg-white rounded-2xl shadow-xl p-7 border border-[#E5E7EB]"
          data-testid="forgot-form-card"
        >
          {submitted ? (
            <div data-testid="forgot-success-state">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#4A7C59] flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-xl text-[#14281D] font-semibold mb-1">
                    Check your email
                  </h2>
                  <p className="text-sm text-[#1F3D2C]/75 leading-snug">
                    If an account exists for that email, we've sent a reset link.
                    Please check your inbox — the link expires in one hour.
                  </p>
                </div>
              </div>
              <Link
                to="/login"
                className="inline-block mt-4 text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-medium text-sm"
                data-testid="forgot-back-to-login-link"
              >
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  className="p-3.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-red-700 text-sm"
                  data-testid="forgot-error-banner"
                >
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div>
                <label
                  className="block text-sm font-medium text-[#14281D] mb-1.5"
                  htmlFor="forgot-email"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  <input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6FBF73] focus:border-transparent text-base"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white py-3 text-base font-semibold rounded-full transition-colors"
                data-testid="forgot-submit-button"
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/login"
                  className="text-[#2F5D3A] hover:text-[#6FBF73] hover:underline font-medium text-sm"
                  data-testid="forgot-cancel-link"
                >
                  ← Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
